import { type JSX } from "react";
import type { McpDocQaData } from "./mcpAudit";
import McpQueriesBlock from "./McpQueriesBlock";

/**
 * Structured rendering of what the doc-q-a MCP server sent back for one call:
 * the queries it ran and the scope/volume they covered.
 *
 * The output row is counts-only by design — each query's distilled answer is
 * stored untruncated on its synthesis LLM request, and the per-chunk relevance
 * verdicts (including the rejected chunks) are on the action row's fan-out
 * detail, both of which the turn detail already renders.
 */
export default function McpDocQaOutput({ data }: { data: McpDocQaData }): JSX.Element {
  // A count is rendered only when the row actually carried it: the server
  // populates all three on every call, so a missing one means a malformed or
  // pre-counts row, and showing 0 there would read as a real empty result.
  const counts: Array<[number, string]> = [];
  if (data.queryCount !== null) counts.push([data.queryCount, "queries"]);
  if (data.chunkCount !== null) counts.push([data.chunkCount, "chunks"]);
  if (data.datasetCount !== null) counts.push([data.datasetCount, "datasets searched"]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
        {counts.map(([value, label]) => (
          <span key={label}>
            <span className="font-medium">{value}</span> {label}
          </span>
        ))}
        {data.distilled && (
          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-xs text-blue-700">
            distilled
          </span>
        )}
      </div>
      {data.error && <p className="text-sm text-red-600">{data.error}</p>}
      <McpQueriesBlock goal={null} queries={data.queries} />
    </div>
  );
}
